import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Users
export const users = {
  getAll: () => api.get('/users'),
};

// Tasks
export const tasks = {
  getAll: () => api.get('/tasks'),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  exportExcel: (status, vertical) => api.get('/tasks/export/excel', { params: { status, vertical }, responseType: 'blob' }),
  updateNotes: (id, notes) => api.post(`/tasks/${id}/notes`, { notes }),
  requestStatusChange: (id, requested_status, reason) => api.post(`/tasks/${id}/request-status-change`, { requested_status, reason }),
  approveStatusChange: (id, requestId, approve) => api.post(`/tasks/${id}/approve-status-change/${requestId}?approve=${approve}`),
};

// Time Tracking
export const time = {
  start: (data) => api.post('/time/start', data),
  stop: (data) => api.post('/time/stop', data),
  getCurrent: () => api.get('/time/current'),
  getEntries: () => api.get('/time/entries'),
};

// Clients (CRM)
export const clients = {
  getAll: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getExchangeRate: () => api.get('/exchange-rate'),
};

// Contacts
export const contacts = {
  getAll: (clientId) => api.get('/contacts', { params: { client_id: clientId } }),
  get: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
};

// Opportunities/Deals
export const opportunities = {
  getAll: (params) => api.get('/opportunities', { params }),
  get: (id) => api.get(`/opportunities/${id}`),
  create: (data) => api.post('/opportunities', data),
  update: (id, data) => api.put(`/opportunities/${id}`, data),
  delete: (id) => api.delete(`/opportunities/${id}`),
  linkQuote: (oppId, quoteId) => api.post(`/opportunities/${oppId}/link-quote?quote_id=${quoteId}`),
};

// Calls
export const calls = {
  getAll: () => api.get('/calls'),
  create: (data) => api.post('/calls', data),
  update: (id, data) => api.put(`/calls/${id}`, data),
};

// Quotes
export const quotes = {
  getAll: () => api.get('/quotes'),
  create: (data) => api.post('/quotes', data),
  updateStatus: (id, status) => api.put(`/quotes/${id}/status?status=${status}`),
  convert: (id) => api.post(`/quotes/${id}/convert`),
  requestApproval: (id) => api.post(`/quotes/${id}/request-approval`),
  getPendingApproval: () => api.get('/quotes/pending-approval'),
  approve: (id, sendToClient = true) => api.post(`/quotes/${id}/approve?send_to_client=${sendToClient}`),
  reject: (id, reason = '') => api.post(`/quotes/${id}/reject?reason=${encodeURIComponent(reason)}`),
};

// Invoices
export const invoices = {
  getAll: () => api.get('/invoices'),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  getPdfData: (id) => api.get(`/invoices/${id}/pdf-data`),
  sendEmail: (id, data) => api.post(`/invoices/${id}/send-email`, { invoice_id: id, ...data }),
  downloadTemplate: () => api.get('/invoices/template/download', { responseType: 'blob' }),
  exportExcel: (status) => api.get('/invoices/export/excel', { params: { status }, responseType: 'blob' }),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/invoices/import/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// NCF Types
export const ncf = {
  getTypes: () => api.get('/ncf/types'),
  getSequences: () => api.get('/ncf/sequences'),
};

// Deletion Requests
export const deletionRequests = {
  create: (itemType, itemId, reason = '') => api.post('/deletion-requests', { 
    item_type: itemType, 
    item_id: itemId, 
    reason 
  }),
  getAll: (status) => api.get('/deletion-requests', { params: { status } }),
  approve: (requestId) => api.post(`/admin/deletion-requests/${requestId}/approve`),
  reject: (requestId, reason = '') => api.post(`/admin/deletion-requests/${requestId}/reject?rejection_reason=${encodeURIComponent(reason)}`),
  getPendingCount: () => api.get('/admin/deletion-requests/pending/count'),
};

// Notifications
export const notifications = {
  getAll: () => api.get('/notifications'),
  sendReminder: (invoiceId) => api.post('/notifications/send-invoice-reminder', { invoice_id: invoiceId }),
  sendBulkReminders: (daysBefore) => api.post('/notifications/send-bulk-reminders', { days_before_due: daysBefore }),
  getPendingInvoices: (days) => api.get(`/notifications/pending-invoices?days=${days || 7}`),
  getHistory: () => api.get('/notifications/history'),
};

// Meetings & Transcription
export const meetings = {
  getAll: () => api.get('/meetings'),
  upload: (formData) => api.post('/meetings/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  transcribe: (file, language) => {
    const formData = new FormData();
    formData.append('file', file);
    if (language) {
      formData.append('language', language);
    }
    return api.post('/meetings/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 min timeout for large files
    });
  },
  extractTasks: (meetingId) => api.post(`/meetings/${meetingId}/extract-tasks`),
  createTasks: (meetingId, tasksData) => api.post(`/meetings/${meetingId}/create-tasks`, tasksData),
  getTranscriptions: () => api.get('/meetings/transcriptions'),
  getTranscription: (id) => api.get(`/meetings/transcriptions/${id}`),
  deleteTranscription: (id) => api.delete(`/meetings/transcriptions/${id}`),
  delete: (id) => api.delete(`/meetings/${id}`),
};

// Hubstaff Integration
export const hubstaff = {
  getStatus: () => api.get('/hubstaff/status'),
  sync: (startDate, endDate) => api.post('/hubstaff/sync', { start_date: startDate, end_date: endDate }),
  getEntries: (params) => api.get('/hubstaff/entries', { params }),
  getSummary: (startDate, endDate) => api.get('/hubstaff/summary', { params: { start_date: startDate, end_date: endDate } }),
};

// Dashboard
export const dashboard = {
  getStats: () => api.get('/dashboard/stats'),
  getToday: () => api.get('/dashboard/today'),
};

// Calendar
export const calendar = {
  getEvents: (startDate, endDate, includeOutlook = true) => 
    api.get('/calendar/events', { params: { start_date: startDate, end_date: endDate, include_outlook: includeOutlook } }),
};

// Outlook Calendar
export const outlook = {
  getEvents: (startDate, endDate) => api.get('/outlook/calendar/events', { params: { start_date: startDate, end_date: endDate } }),
  createEvent: (data) => api.post('/outlook/calendar/events', data),
  deleteEvent: (eventId) => api.delete(`/outlook/calendar/events/${eventId}`),
  getSyncStatus: () => api.get('/outlook/calendar/sync-status'),
};

// Expenses (Costos)
export const expenses = {
  getAll: (params) => api.get('/expenses', { params }),
  get: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getCategories: () => api.get('/expenses/categories'),
  getSummaryByCategory: (year, month) => api.get('/expenses/summary/by-category', { params: { year, month } }),
  importExternal: (expensesData, sourceApp) => api.post('/expenses/import-external', expensesData, { params: { source_app: sourceApp } }),
  downloadTemplate: () => api.get('/expenses/template/download', { responseType: 'blob' }),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/expenses/import/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Budgets
export const budgets = {
  getAll: (params) => api.get('/budgets', { params }),
  create: (data) => api.post('/budgets', data),
  delete: (id) => api.delete(`/budgets/${id}`),
  getComparison: (year, month) => api.get('/budgets/comparison', { params: { year, month } }),
};

// Settings
export const settings = {
  getNcfSettings: () => api.get('/settings/ncf'),
  updateNcfSequence: (ncfType, newSequence) => api.put(`/settings/ncf/sequence/${ncfType}?new_sequence=${newSequence}`),
};

// Admin - User Management
export const admin = {
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deactivateUser: (userId) => api.delete(`/admin/users/${userId}`),
  reactivateUser: (userId) => api.post(`/admin/users/${userId}/reactivate`),
  getDeletionRequests: (status) => api.get('/deletion-requests', { params: { status } }),
  approveDeletion: (requestId) => api.post(`/admin/deletion-requests/${requestId}/approve`),
  rejectDeletion: (requestId, reason) => api.post(`/admin/deletion-requests/${requestId}/reject`, null, { params: { rejection_reason: reason } }),
  getPendingDeletionsCount: () => api.get('/admin/deletion-requests/pending/count'),
};

// AI Planning
export const aiPlanning = {
  getTaskSuggestions: () => api.get('/ai/task-suggestions'),
  autoPrioritize: () => api.post('/ai/auto-prioritize'),
  chat: (message, sessionId) => api.post('/ai/chat', { message, session_id: sessionId }),
  getChatHistory: (sessionId) => api.get('/ai/chat/history', { params: { session_id: sessionId } }),
  getChatSessions: () => api.get('/ai/chat/sessions'),
  deleteChatSession: (sessionId) => api.delete(`/ai/chat/sessions/${sessionId}`),
};

// Productivity
export const productivity = {
  getPersonal: (period) => api.get('/productivity/personal', { params: { period } }),
  getTeam: (period) => api.get('/productivity/team', { params: { period } }),
  getInsights: () => api.get('/productivity/insights'),
  getTeamDashboard: (period) => api.get('/productivity/team-dashboard', { params: { period } }),
  updateTeamMembers: (members) => api.post('/productivity/team-members', members),
  exportExcel: (period) => api.get('/productivity/export/excel', { params: { period }, responseType: 'blob' }),
  sync: () => api.post('/productivity/sync'),
};

// Task Notifications
export const taskNotifications = {
  sendReminders: (daysThreshold = 3) => api.post(`/notifications/send-task-reminders?days_threshold=${daysThreshold}`),
  getAlerts: (daysThreshold = 3) => api.get(`/notifications/task-alerts?days_threshold=${daysThreshold}`),
};

// Microsoft Integration
export const microsoftIntegration = {
  getStatus: () => api.get('/microsoft/status'),
  configure: (config) => api.post('/microsoft/configure', config),
};

export default api;
